import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { Currency, WalletService } from '../views/user-management/wallet.service';
import { isReportCurrency, REPORT_CURRENCY_QUERY, ReportCurrencyContext } from './report-currency';

@Component({
  selector: 'app-report-currency',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoDirective],
  template: `
    <ng-container *transloco="let t">
      <section *ngIf="visible" class="border rounded p-3 mb-3 d-flex flex-wrap gap-3 align-items-center" aria-labelledby="reportCurrencyLabel">
        <label id="reportCurrencyLabel" for="reportCurrency" class="fw-semibold">{{ t('reportCurrency.label') }}</label>
        <select id="reportCurrency" class="form-select w-auto" [ngModel]="context.currency || ''" (ngModelChange)="changeCurrency($event)">
          <option value="" disabled>{{ t('reportCurrency.select') }}</option>
          <option *ngFor="let currency of currencies" [value]="currency.code">{{ currency.code }} · {{ currency.name }}</option>
          <option value="UNKNOWN">{{ t('reportCurrency.unknown') }}</option>
        </select>
        <span class="text-body-secondary small">{{ t(context.currency ? 'reportCurrency.hint' : 'reportCurrency.required') }}</span>
        <span *ngIf="loadError" role="alert" class="text-danger small">{{ t('reportCurrency.loadFailed') }}</span>
      </section>
    </ng-container>
  `
})
export class ReportCurrencyComponent implements OnInit {
  readonly context = inject(ReportCurrencyContext);
  private readonly router = inject(Router);
  private readonly walletService = inject(WalletService);
  private readonly destroyRef = inject(DestroyRef);
  currencies: Currency[] = [];
  loadError = false;

  get visible(): boolean {
    return /^\/(analytics|crm\/(players|segments))(\/|\?|$)/.test(this.router.url);
  }

  ngOnInit(): void {
    const syncQuery = () => {
      const params = this.router.getCurrentNavigation()?.finalUrl?.queryParams ?? this.router.parseUrl(this.router.url).queryParams;
      if (isReportCurrency(params[REPORT_CURRENCY_QUERY])) this.context.select(params[REPORT_CURRENCY_QUERY]);
    };
    syncQuery();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef)).subscribe(syncQuery);
    this.walletService.getCurrencies().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: currencies => { this.currencies = currencies.filter(currency => isReportCurrency(currency.code)); },
      error: () => { this.loadError = true; }
    });
  }

  changeCurrency(currency: string): void {
    if (!isReportCurrency(currency)) return;
    this.context.select(currency);
    void this.router.navigate([], { queryParams: { [REPORT_CURRENCY_QUERY]: currency }, queryParamsHandling: 'merge' });
  }
}
